import { selectionTypes } from 'Components/Reports/values/enums';
import { getUserData } from 'Services/API';
import { localesUnderscoreMap } from '../../constants/constants';
import { getCurriculumOptions } from '../../components/Reports/containers/ReportSettings/SelectSkillsCurriculum/getCurriculumOptions';

// Define "combo" types for METRIC / SCALE / BENCHMARK and SCHOOL / CLASS / STUDENT
const MSB_TYPE = selectionTypes.METRIC + selectionTypes.SCALE + selectionTypes.BENCHMARK;
const ROSTER_TYPE = selectionTypes.SCHOOL + selectionTypes.CLASS + selectionTypes.STUDENT;

const EQUATING_TYPE = 'EQUATING';
const SHOW_ASSESSMENTS = 'SHOW_ASSESSMENTS';
const LOCALE = 'LOCALE';
const READING_RISK_TYPES = 'READING_RISK_TYPES';
const SCREENING_WINDOW = 'SCREENING_WINDOW';
const STATE_PERCENTILE_CUTS = 'STATE_PERCENTILE_CUTS';
const ASSESSMENT_TYPE_FILTER = 'ASSESSMENT_TYPE_FILTER';
const REPORT_CONFIG = 'REPORT_CONFIG';
const CURRICULUM = 'CURRICULUM';

// Store a recent selection
function storeRecentSelection(selections, selectionKey) {
  let userKey = selectionKey + getUserData().userId + JSON.stringify(getUserData().sorConfig); // append userId
  sessionStorage.setItem(userKey, JSON.stringify({ // selectionType as key
    timestamp: new Date(),
    content: selections,
  }));
}

// Fetch a recent selection
function fetchRecentSelection(selectionKey) {
  let userKey = selectionKey + getUserData().userId + JSON.stringify(getUserData().sorConfig); // append userId
  let rawSelection = sessionStorage.getItem(userKey);
  let selection = null;
  if(rawSelection && !hasTimedOut(rawSelection, userKey)){
    selection = JSON.parse(rawSelection).content;
  }
  return(selection); // TODO consider doing some validation on selection
}

// Returns true if the content is more than two months old
// TODO talk through an appropriate timeout with the team
export function hasTimedOut(rawMetadata, key) {
  let data = JSON.parse(rawMetadata);

  // Calculate expiration time for content, to force periodic refresh after 2 months
  let now = new Date();
  let expiration = new Date(data.timestamp);
  expiration.setMonth(expiration.getMonth() + 2);

  // Ditch the content if too old
  if (now.getTime() > expiration.getTime()) {
      sessionStorage.removeItem(key);
      return true;
  }
  return false;
}

// Metric / Scale / Benchmark
export function getMostRecentMSB() {
  return fetchRecentSelection(MSB_TYPE);
}
export function setMostRecentMSB(msbSelections) {
  storeRecentSelection(msbSelections, MSB_TYPE);
}

// Benchmark Report
export function getMostRecentAssessmentTypeFilter() {
  return fetchRecentSelection(ASSESSMENT_TYPE_FILTER);
}
export function setMostRecentAssessmentTypeFilter(assessmentTypeSelection) {
  storeRecentSelection(assessmentTypeSelection, ASSESSMENT_TYPE_FILTER);
}

// Equating
export function getMostRecentEquating() {
  return fetchRecentSelection(EQUATING_TYPE);
}
export function setMostRecentEquating(equatingSelection) {
  storeRecentSelection(equatingSelection, EQUATING_TYPE);
}

// Roster
export function getMostRecentRoster() {
  return fetchRecentSelection(ROSTER_TYPE);
}
export function setMostRecentRoster(rosterSelections) {
  storeRecentSelection(rosterSelections, ROSTER_TYPE);

  // If applicable, set cache for school/class and class/student pairs
  if (rosterSelections.school && rosterSelections.classroom) {
    setMostRecentParentChildPair(rosterSelections.school.value, rosterSelections.classroom, 'classroom');
    if(rosterSelections.classroom && rosterSelections.student) {
      setMostRecentParentChildPair(rosterSelections.classroom.value, rosterSelections.student, 'student');
    }
  }
}

// Parent Child Pairings (e.g. district/school, school/class, and class/student)
export function getMostRecentParentChildPair(parentId, type) {
  // generate key from child type + parentId
  const key = 'mostRecentPairingFor' + type + parentId;
  return fetchRecentSelection(key);
}
export function setMostRecentParentChildPair(parentId, child, type) {
  // generate key from child type + parentId
  const key = 'mostRecentPairingFor' + type + parentId;
  storeRecentSelection(child, key);
}

// Show assessments
export function getMostRecentShowAssessments() {
  return fetchRecentSelection(SHOW_ASSESSMENTS);
}
export function setMostRecentShowAssessments(showAssessments) {
  storeRecentSelection(showAssessments, SHOW_ASSESSMENTS);
}

// Locale
export function getMostRecentLocale() {
  return fetchRecentSelection(LOCALE);
}

export function setMostRecentLocale(locale) {
  storeRecentSelection(locale, LOCALE);
}

// Reading risk types
export function getMostRecentReadingRisks() {
  return fetchRecentSelection(READING_RISK_TYPES);
}

export function setMostRecentReadingRisks(riskTypes) {
  storeRecentSelection(riskTypes, READING_RISK_TYPES);
}

// Screening window
export function getMostRecentScreeningWindow() {
  return fetchRecentSelection(SCREENING_WINDOW);
}

export function setMostRecentScreeningWindow(screeningWindow) {
  storeRecentSelection(screeningWindow, SCREENING_WINDOW);
}

// State Percentile Cuts
export function getMostRecentStatePercentileCuts() {
  return fetchRecentSelection(STATE_PERCENTILE_CUTS);
}

export function setMostRecentStatePercentileCuts(statePercentileCuts) {
  storeRecentSelection(statePercentileCuts, STATE_PERCENTILE_CUTS);
}

// Report config
export function getMostRecentReportConfig() {
  return fetchRecentSelection(REPORT_CONFIG);
}

export function setMostRecentReportConfig(reportConfig) {
  storeRecentSelection(reportConfig, REPORT_CONFIG);
}

export function getMostRecentCurriculum(locale) {
  const lastSelectedCurriculum =  fetchRecentSelection(CURRICULUM);
  const configuredCurriculum = getUserData().curriculum;
  
  // available options = configured curriculum for the locale + default curriculum for the locale
  const availableCurriculumOptions = getCurriculumOptions(configuredCurriculum, locale);

  // default option = last selected option || configured option for the locale || default option for the locale
  const localeKey = localesUnderscoreMap[locale];
  const defaultConfiguredCurriculumId = configuredCurriculum?.find(c => c.locale === localeKey)?.curriculumId;
  const lastSelectedCurriculumOption = availableCurriculumOptions.find(o => o.locale === lastSelectedCurriculum?.locale && o.value === lastSelectedCurriculum.value);
  const defaultConfiguredCurriculumOption = availableCurriculumOptions.find(o => o.locale === localeKey && o.value === defaultConfiguredCurriculumId);
  const defaultSelectedCurriculumOption = availableCurriculumOptions[0];

  return lastSelectedCurriculumOption || defaultConfiguredCurriculumOption || defaultSelectedCurriculumOption;
}

export function setMostRecentCurriculum(curriculum) {
  storeRecentSelection(curriculum, CURRICULUM);
}
